import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { ErrorOutline, InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import { BannerProps, BannerSeverity } from './types';

const getBannerStyles = (severity: BannerSeverity) => {
  const styles = {
    error: {
      backgroundColor: abyssColors.error[100],
      iconColor: abyssColors.error[700],
      textColor: abyssColors.error[700],
    },
    warning: {
      backgroundColor: abyssColors.warning[100],
      iconColor: abyssColors.warning[700],
      textColor: abyssColors.warning[700],
    },
    info: {
      backgroundColor: abyssColors.primary[100],
      iconColor: abyssColors.primary[700],
      textColor: abyssColors.primary[700],
    },
    success: {
      backgroundColor: abyssColors.success[100],
      iconColor: abyssColors.success[700],
      textColor: abyssColors.success[700],
    },
  };

  return styles[severity];
};

const getIcon = (severity: BannerSeverity, iconColor: string) => {
  const iconProps = { sx: { color: iconColor, fontSize: '2.2rem' } };

  const icons = {
    error: <ErrorOutline {...iconProps} />,
    warning: <WarningAmberOutlined {...iconProps} />,
    info: <InfoOutlined {...iconProps} />,
    success: <AbyssIcon iconName="check-success" style={{ height: '2.2rem', width: '2.2rem' }} />,
  };

  return icons[severity];
};

export const Banner = ({ severity, message, action }: BannerProps) => {
  const styles = getBannerStyles(severity);

  return (
    <Alert
      severity={severity}
      icon={getIcon(severity, styles.iconColor)}
      action={
        action ? (
          <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>{action}</Box>
        ) : undefined
      }
      sx={{
        width: '100%',
        backgroundColor: `${styles.backgroundColor} !important`,
        border: 'none !important',
        borderRadius: '0.4rem !important',
        padding: '1.6rem !important',
        boxShadow: 'none !important',
        '& .MuiAlert-icon': {
          padding: '0 !important',
          margin: '0 !important',
          marginRight: '1.2rem !important',
          display: 'flex',
          alignItems: 'center',
          opacity: '1 !important',
        },
        '& .MuiAlert-message': {
          padding: '0 !important',
          display: 'flex',
          alignItems: 'center',
          flex: 1,
        },
        '& .MuiAlert-action': {
          padding: '0 !important',
          margin: '0 !important',
          marginRight: '0 !important',
          marginLeft: 'auto',
          paddingLeft: '1.6rem !important',
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: `${styles.textColor} !important`,
          fontSize: '1.4rem !important',
          fontWeight: '500 !important',
          lineHeight: '2rem',
        }}
      >
        {message}
      </Typography>
    </Alert>
  );
};
